<template>
    <div id="mothistory2" class="pb-20 pt-20" v-show="active">
        <div class="row">
            <div class="col-3-md d-none d-block-md p-30 pt-0 text-center">
                <p class="text-bold mb-30">MOT status:</p>
                <graph :small="false" :expired="isExpired" :days="daysLeft" label="MOT"></graph>
            </div>
            <div class="col-9-md">
                <div class="panel-header text-center">
                    MOT history
                </div>
                <div v-if="loading" class="text-center">
                    Loading...
                </div>

                <div v-if="mot.length" v-for="item in mot">
                    <div class="row mt-20 centered">
                        <div class="mb-10 col-2-sm col-4 mh__result mh__result--pass"
                             v-if="item.result === 'Pass'">
                            {{ item.result }}
                        </div>
                        <div class="mb-10 col-2-sm col-4 mh__result mh__result--fail"
                             v-if="item.result !== 'Pass'">
                            {{ item.result }}
                        </div>

                        <div class="mb-10 col-4-sm col-8">
                            <div class="mh__title">
                                Date tested
                            </div>
                            <div class="mh__value">
                                {{ cleanDate(item.mot_date) }}
                            </div>
                        </div>

                        <div class="mb-10 col-2-sm col-4">
                            <div class="mh__title">
                                Mileage
                            </div>
                            <div class="mh__value">
                                {{ formatThousandsWithRounding(parseInt(item.odometer_reading), 0) }}
                            </div>
                        </div>

                        <div class="mb-10 col-4-sm col-8">
                            <div class="mh__title">
                                Expiry date
                            </div>
                            <div class="mh__value">
                                {{ cleanDate(item.exp_date) }}
                            </div>
                        </div>
                    </div>

                    <div class="row s-row pb-20">
                        <div class="offset-2-sm col-10-sm"
                             v-if="item.comments && item.comments.reason && item.comments.reason.length > 0">
                            <div class="mh__reason-cat">Reason(s) for failure:</div>
                            <br>
                            <ul class="mh__reasons">
                                <li v-for="advice in item.comments.reason" v-html="advice">
                                </li>
                            </ul>
                        </div>
                        <div class="offset-2-sm col-10-sm"
                             v-if="item.comments.dangerous && item.comments.dangerous.length > 0">
                            <div class="mh__reason-cat">Dangerous failure(s):</div>
                            <br>
                            <ul class="mh__reasons">
                                <li v-for="advice in item.comments.dangerous" v-html="advice">
                                </li>
                            </ul>
                        </div>
                        <div class="offset-2-sm col-10-sm"
                             v-if="item.comments.major && item.comments.major.length > 0">
                            <div class="mh__reason-cat">Major failure(s):</div>
                            <br>
                            <ul class="mh__reasons">
                                <li v-for="advice in item.comments.major" v-html="advice">
                                </li>
                            </ul>
                        </div>
                        <div class="offset-2-sm col-10-sm"
                             v-if="item.comments.minor && item.comments.minor.length > 0">
                            <div class="mh__reason-cat">Minor failure(s):</div>
                            <br>
                            <ul class="mh__reasons">
                                <li v-for="advice in item.comments.minor" v-html="advice">
                                </li>
                            </ul>
                        </div>
                        <div class="offset-2-sm col-10-sm"
                             v-if="item.comments.advice && item.comments.advice.length > 0">
                            <div class="mh__reason-cat">Advisory notice item(s):</div>
                            <br>
                            <ul class="mh__reasons">
                                <li v-for="advice in item.comments.advice" v-html="advice">
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div v-if="failed" class="text-left">
                    Unfortunately our system cannot find any MOT history for this vehicle.
                </div>

                <div class="pt-20 pb-20" v-if="expired && mot">
                    <div class="alert alert--danger">
                        <strong>This vehicle's MOT has expired</strong><br>
                        You can be fined up to £1000 for driving without a valid MOT.
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import formatThousandsWithRounding from '../modules/formatThousandsWithRounding';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import axiosClient from '../modules/axiosClient';
import Graph from './new-garage/graph.vue';

dayjs.extend(customParseFormat);

export default defineComponent({
    props: {
        'active': {type: Boolean, default: false},
        'make': {type: String},
        'vrm': {type: String},
        'vid': {type: String},
    },
    components: {
        'graph': Graph,
    },
    data() {
        return {
            'mot': [],
            'loading': false,
            'failed': false,
            'message': '',
            'open': [],
            'motData': {},
            'expireMessage': '',
            'expired': false,
            'overlayText': '',
            'overlayTextBold': '',
            'showPlaceholder': false,

            'motExpireDate': '',
        };
    },

    watch: {
        active(motActive: boolean) {
            if (motActive && !this.mot.length) {
                this.loadMotData();
            }
        },
    },
    computed: {
        daysLeft(): number {
            const dueDate = dayjs(this.motExpireDate);
            const today = dayjs().startOf('day');
            if (!dueDate.isValid() || dueDate.isBefore(today)) {
                return 0;
            }

            return dueDate.diff(today, 'day');
        },
        isExpired(): boolean {
            const dueDate = dayjs(this.motExpireDate);
            const today = dayjs().startOf('day');
            if (!dueDate.isValid()) {
                return false;
            }

            return dueDate.isBefore(today);
        },
    },
    methods: {
        loadMotData() {
            this.loading = true;

            axiosClient.post('/api/mot/history', {
                'vrm': this.vrm,
                'make': this.make,
                'all': 1,
            }).then((response) => {
                this.mot = response.data;
                if (typeof response.data[0] != 'undefined' && response.data[0].exp_date) {
                    this.motExpireDate = response.data[0].exp_date;
                }
            }, (response) => {
                console.log(response);

                this.failed = true;
                this.message = 'No Data Available for this vehicle.';
            }).finally(() => {
                this.loading = false;
            });
        },

        formatThousandsWithRounding,
        cleanDate(date: string|null) {
            if (typeof date !== 'string') {
                return 'None';
            }

            if (date.indexOf('0000-00-00') !== -1) {
                return 'None';
            }

            return dayjs(date).format('D/MM/YYYY');
        },
    },
});
</script>
